import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowRight, Calculator, TrendingUp } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { CalculadoraSkeleton } from "@/components/skeletons";
import { BackToTop } from "@/components/BackToTop";
import { ArticleCTA } from "@/components/ArticleCTA";
import { seo, SITE_URL } from "@/lib/seo";
import { projectWealth } from "@/lib/investment-calculations";

const PAGE_TITLE = "Calculadora de Juros Compostos com Aportes Mensais";
const PAGE_DESCRIPTION =
  "Simule mês a mês quanto seu dinheiro rende com juros compostos: informe capital inicial, aporte mensal, taxa e prazo e veja o montante final e os juros ganhos.";

export const Route = createFileRoute("/calculadoras/juros-compostos")({
  head: () => {
    const base = seo({
      path: "/calculadoras/juros-compostos",
      title: `${PAGE_TITLE} — Seu Bolso Tranquilo`,
      description: PAGE_DESCRIPTION,
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Calculadoras", path: "/calculadoras" },
        { name: "Juros Compostos", path: "/calculadoras/juros-compostos" },
      ],
    });
    base.scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Calculadora de Juros Compostos",
        url: `${SITE_URL}/calculadoras/juros-compostos`,
        applicationCategory: "FinanceApplication",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: "0", priceCurrency: "BRL" },
        description: PAGE_DESCRIPTION,
      }),
    });
    base.scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "O que são juros compostos?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Juros compostos são juros que incidem sobre o capital inicial somado aos juros acumulados em períodos anteriores. É o famoso 'juros sobre juros', que faz o patrimônio crescer de forma exponencial ao longo do tempo.",
            },
          },
          {
            "@type": "Question",
            name: "Como calcular juros compostos?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "A fórmula é M = C × (1 + i)ⁿ, onde M é o montante, C o capital inicial, i a taxa de juros por período e n o número de períodos. Com aportes mensais, soma-se o valor futuro de cada aporte.",
            },
          },
          {
            "@type": "Question",
            name: "Qual a diferença entre juros simples e compostos?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Nos juros simples, os juros incidem apenas sobre o capital inicial. Nos compostos, incidem sobre o capital inicial e sobre os juros já acumulados, gerando crescimento exponencial.",
            },
          },
        ],
      }),
    });
    return base;
  },
  component: CompoundInterestPage,
  pendingComponent: CalculadoraSkeleton,
});

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

function CompoundInterestPage() {
  const [initial, setInitial] = useState(1000);
  const [monthly, setMonthly] = useState(500);
  const [annualRate, setAnnualRate] = useState(10);
  const [years, setYears] = useState(10);

  const result = useMemo(() => {
    const months = Math.max(0, Math.round(years * 12));
    const futureValue = projectWealth(initial, monthly, annualRate, months);
    const invested = initial + monthly * months;
    const interest = futureValue - invested;
    const rows: { year: number; balance: number; invested: number; interest: number }[] = [];
    const yearsInt = Math.min(50, Math.max(1, Math.round(years)));
    for (let y = 1; y <= yearsInt; y += 1) {
      const m = y * 12;
      const bal = projectWealth(initial, monthly, annualRate, m);
      const inv = initial + monthly * m;
      rows.push({ year: y, balance: bal, invested: inv, interest: bal - inv });
    }
    return { futureValue, invested, interest, rows };
  }, [initial, monthly, annualRate, years]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-8" alt="Logo Seu Bolso Tranquilo" />
            <span className="font-semibold">Seu Bolso Tranquilo</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
            <Link to="/aprenda" className="hover:text-foreground transition-colors">Aprenda</Link>
            <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
          </nav>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Começar grátis
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">Início</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Calculadora de Juros Compostos</span>
        </nav>

        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
            <Calculator className="size-4" />
            Calculadora gratuita
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Calculadora de Juros Compostos
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Descubra quanto seu dinheiro pode render ao longo do tempo. Informe o capital inicial,
            os aportes mensais, a taxa de juros anual e o prazo — simule sua projeção em segundos.
          </p>
        </div>

        <section className="grid lg:grid-cols-[1fr_1.2fr] gap-6 mb-12">
          <form
            className="rounded-3xl border border-border/60 bg-card p-6 sm:p-8 space-y-5"
            onSubmit={(e) => e.preventDefault()}
            aria-label="Parâmetros da simulação"
          >
            <h2 className="text-lg font-semibold mb-2">Parâmetros</h2>

            <label className="block">
              <span className="text-sm font-medium mb-1.5 block">Capital inicial (R$)</span>
              <input
                type="number" min={0} step="100" value={initial}
                onChange={(e) => setInitial(Math.max(0, Number(e.target.value) || 0))}
                className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-base"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium mb-1.5 block">Aporte mensal (R$)</span>
              <input
                type="number" min={0} step="50" value={monthly}
                onChange={(e) => setMonthly(Math.max(0, Number(e.target.value) || 0))}
                className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-base"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium mb-1.5 block">Taxa de juros (% ao ano)</span>
              <input
                type="number" min={0} step="0.1" value={annualRate}
                onChange={(e) => setAnnualRate(Math.max(0, Number(e.target.value) || 0))}
                className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-base"
              />
              <span className="text-xs text-muted-foreground mt-1 block">
                Referência: CDI ~10,5%, Tesouro IPCA+ ~6% + inflação, Poupança ~6,17%.
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium mb-1.5 block">Prazo (anos)</span>
              <input
                type="number" min={1} max={50} step="1" value={years}
                onChange={(e) => setYears(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-base"
              />
            </label>
          </form>

          <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-primary-glow/5 p-6 sm:p-8" aria-live="polite">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-3">
              <TrendingUp className="size-4" />
              Resultado da simulação
            </div>
            <div className="mb-6">
              <div className="text-sm text-muted-foreground">Valor acumulado em {years} {years === 1 ? "ano" : "anos"}</div>
              <div className="text-4xl sm:text-5xl font-bold tracking-tight mt-1">{brl(result.futureValue)}</div>
            </div>
            <dl className="grid grid-cols-2 gap-4 mb-6">
              <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
                <dt className="text-xs text-muted-foreground">Total investido</dt>
                <dd className="text-xl font-semibold mt-1">{brl(result.invested)}</dd>
              </div>
              <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
                <dt className="text-xs text-muted-foreground">Juros acumulados</dt>
                <dd className="text-xl font-semibold mt-1 text-primary">{brl(result.interest)}</dd>
              </div>
            </dl>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Acompanhar meus investimentos
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </section>

        <section className="rounded-3xl border border-border/60 bg-card p-6 sm:p-8 mb-12 overflow-x-auto">
          <h2 className="text-lg font-semibold mb-4">Evolução ano a ano</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border/60">
                <th className="py-2 pr-4 font-medium">Ano</th>
                <th className="py-2 pr-4 font-medium">Investido</th>
                <th className="py-2 pr-4 font-medium">Juros</th>
                <th className="py-2 font-medium">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.year} className="border-b border-border/30 last:border-0">
                  <td className="py-2 pr-4">{r.year}</td>
                  <td className="py-2 pr-4">{brl(r.invested)}</td>
                  <td className="py-2 pr-4 text-primary">{brl(r.interest)}</td>
                  <td className="py-2 font-medium">{brl(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <ArticleCTA
          variant="final"
          headline="Pare de simular e comece a investir de verdade"
          body="Crie sua conta grátis no Seu Bolso Tranquilo e acompanhe seus investimentos em renda fixa, ações e FIIs, com projeções automáticas, benchmarks e análise por IA."
        />

        <section className="prose prose-invert max-w-3xl mx-auto mt-12">
          <h2>O que são juros compostos?</h2>
          <p>
            Juros compostos são juros que incidem sobre o capital inicial somado aos juros já
            acumulados nos períodos anteriores — o famoso "juros sobre juros". É o mecanismo que faz
            o patrimônio crescer de forma exponencial ao longo do tempo, e por isso Einstein o
            chamou de "a oitava maravilha do mundo".
          </p>
          <h2>Fórmula dos juros compostos</h2>
          <p>
            A fórmula clássica é <strong>M = C × (1 + i)<sup>n</sup></strong>, onde M é o montante
            final, C é o capital inicial, i a taxa de juros por período e n o número de períodos.
            Quando há aportes mensais, soma-se o valor futuro de cada contribuição.
          </p>
          <h2>Juros simples vs juros compostos</h2>
          <p>
            Nos juros simples, o rendimento incide só sobre o capital inicial. Nos compostos, incide
            sobre o capital mais os juros acumulados — a diferença depois de alguns anos é enorme.
          </p>
          <h2>Como usar esta calculadora</h2>
          <ol>
            <li>Informe seu <strong>capital inicial</strong> — o valor que você já tem hoje.</li>
            <li>Defina o <strong>aporte mensal</strong> que pretende fazer.</li>
            <li>Escolha uma <strong>taxa de juros</strong> realista para o ativo (CDI, IPCA+, etc.).</li>
            <li>Selecione o <strong>prazo em anos</strong> e veja a projeção atualizar em tempo real.</li>
          </ol>
        </section>
      </main>

      <BackToTop />
    </div>
  );
}
